import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Mobile quantity selector for product grid cards.
 * Markup comes from snippets/add-to-cart-button.liquid (qty_selector: true).
 * State always follows the real cart, so drawer changes, page refresh
 * and back/forward navigation all stay in sync.
 */
if (!window.__gridQtyInit) {
  window.__gridQtyInit = true;

  const mq = window.matchMedia('(max-width: 749px)');
  const SELECTOR = '.grid-qty[data-variant-id]';
  const CHECKMARK_MS = 900; // checkmark animation kitni der dikhe, uske baad selector aaye (ms)

  /** @type {Array<{variant_id: number, quantity: number}>} */
  let lastItems = [];

  const root = () => window.Shopify?.routes?.root || '/';
  const cartChangeUrl = () => window.Theme?.routes?.cart_change_url || root() + 'cart/change.js';

  // Mobile par add to cart ke baad cart drawer auto-open na ho
  function syncCartAutoOpen() {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    if (drawer.dataset.autoOpenDefault === undefined) {
      drawer.dataset.autoOpenDefault = drawer.hasAttribute('auto-open') ? 'true' : 'false';
    }
    if (mq.matches) drawer.removeAttribute('auto-open');
    else if (drawer.dataset.autoOpenDefault === 'true') drawer.setAttribute('auto-open', '');
  }
  syncCartAutoOpen();

  /**
   * Add-to-cart click ke baad: checkmark animation poori dikhao, phir seedha selector.
   * Beech me "Add to cart" text ka flash nahi aata.
   * @param {HTMLElement} wrap @param {number} qty
   */
  function renderWrap(wrap, qty) {
    if (wrap.classList.contains('is-adding')) {
      if (qty <= 0) return; // cart abhi update nahi hua, button waisa hi rehne do
      const elapsed = performance.now() - Number(wrap.dataset.addStart || 0);
      clearTimeout(wrap._swapTimeout);
      wrap._swapTimeout = setTimeout(() => {
        wrap.classList.remove('is-adding');
        applyQty(wrap, qty);
      }, Math.max(0, CHECKMARK_MS - elapsed));
      return;
    }
    applyQty(wrap, qty);
  }

  /** @param {HTMLElement} wrap @param {number} qty */
  function applyQty(wrap, qty) {
    wrap.classList.toggle('has-qty', mq.matches && qty > 0);
    if (qty <= 0) return;

    const max = parseInt(wrap.dataset.max, 10);
    const left = /** @type {HTMLElement} */ (wrap.querySelector('.grid-qty__minus-delete'));
    const plus = /** @type {HTMLButtonElement} */ (wrap.querySelector('.grid-qty__plus'));

    wrap.querySelector('.grid-qty__count').textContent = String(qty);
    left.dataset.mode = qty <= 1 ? 'delete' : 'minus';
    left.setAttribute('aria-label', qty <= 1 ? 'Remove item' : 'Decrease quantity');
    plus.disabled = !isNaN(max) && qty >= max; // stock limit
  }

  /** @param {Array<{variant_id: number, quantity: number}>} items */
  function syncAll(items) {
    lastItems = items;
    document.querySelectorAll(SELECTOR).forEach((wrap) => {
      const item = items.find((i) => String(i.variant_id) === /** @type {HTMLElement} */ (wrap).dataset.variantId);
      renderWrap(/** @type {HTMLElement} */ (wrap), item ? item.quantity : 0);
    });
  }

  function refreshFromCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((cart) => syncAll(cart.items || []))
      .catch(() => {});
  }

  // 1. Page load + breakpoint change + back/forward cache
  if (mq.matches) refreshFromCart();
  mq.addEventListener('change', () => {
    syncCartAutoOpen();
    if (mq.matches) refreshFromCart();
    else syncAll(lastItems); // desktop: has-qty hata do
  });
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) syncCartAutoOpen();
    if (e.persisted && mq.matches) refreshFromCart();
  });

  // 2. Cart kisi bhi jagah se badle (add button, drawer, product page) -> sync
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    syncCartAutoOpen(); // drawer ke handle karne se pehle auto-open hata do (mobile)
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) {
          document.querySelectorAll('.grid-qty.is-adding').forEach((w) => w.classList.remove('is-adding'));
        }
        if (Array.isArray(detail?.items)) syncAll(detail.items);
        else refreshFromCart();
      })
      .catch(() => {});
  });

  // 3. Grid re-render (filters, infinite scroll, morph) ke baad naye cards sync karo
  let observerTimeout;
  new MutationObserver((mutations) => {
    const hasNewCards = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) => n.nodeType === 1 && (/** @type {Element} */ (n).matches?.(SELECTOR) || /** @type {Element} */ (n).querySelector?.(SELECTOR))
      )
    );
    if (!hasNewCards) return;
    clearTimeout(observerTimeout);
    observerTimeout = setTimeout(() => syncAll(lastItems), 50);
  }).observe(document.body, { childList: true, subtree: true });

  // 4. Add to cart click (mobile): checkmark chalne tak button rakho, cart update ke baad selector
  document.addEventListener('click', (e) => {
    const btn = /** @type {Element} */ (e.target).closest('.grid-qty add-to-cart-component button[name="add"]');
    if (!btn || !mq.matches || /** @type {HTMLButtonElement} */ (btn).disabled) return;

    const wrap = /** @type {HTMLElement} */ (btn.closest('.grid-qty'));
    wrap.classList.add('is-adding');
    wrap.dataset.addStart = String(performance.now());
    clearTimeout(wrap._addTimeout);
    wrap._addTimeout = setTimeout(() => wrap.classList.remove('is-adding'), 5000); // safety: error/slow network
  });

  // 5. Plus / minus / delete clicks
  document.addEventListener('click', (e) => {
    const target = /** @type {Element} */ (e.target);
    const plus = target.closest('.grid-qty__plus');
    const left = /** @type {HTMLElement | null} */ (target.closest('.grid-qty__minus-delete'));
    if (!plus && !left) return;

    const wrap = /** @type {HTMLElement | null} */ (target.closest('.grid-qty'));
    if (!wrap || wrap.dataset.updating === 'true') return;

    const current = Number(wrap.querySelector('.grid-qty__count').textContent) || 1;
    const max = parseInt(wrap.dataset.max, 10);

    let next;
    if (plus) {
      if (!isNaN(max) && current >= max) return;
      next = current + 1;
    } else {
      next = left.dataset.mode === 'delete' ? 0 : current - 1;
    }

    wrap.dataset.updating = 'true';
    updateLine(wrap.dataset.variantId, next, wrap).finally(() => {
      wrap.dataset.updating = 'false';
    });
  });

  /** @param {string} variantId @param {number} quantity @param {HTMLElement} sourceEl */
  function updateLine(variantId, quantity, sourceEl) {
    const deferred = CartLinesUpdateEvent.createPromise();
    const target = sourceEl.closest('product-form-component') || sourceEl;

    target.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity === 0 ? 'remove' : 'update',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    return fetch(cartChangeUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    })
      .then((res) => res.text())
      .then((text) => {
        let cart;
        try {
          cart = JSON.parse(text);
        } catch (err) {
          throw new Error('Non-JSON response from cart/change');
        }

        if (cart.status) {
          // e.g. stock error: items nahi bhej rahe, listener real cart fetch karega
          console.error('Cart update failed:', cart.description);
          deferred.resolve({ cart: null, detail: { didError: true, source: 'qty-selector' } });
          return;
        }

        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: false, items: cart.items, source: 'qty-selector' },
        });
      })
      .catch((err) => {
        console.error('Cart update error:', err);
        deferred.reject(err);
        refreshFromCart();
      });
  }
}